import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useState } from 'react';
import { Alert, Card, TabBar } from '@thegpvc/ui';
import { api } from '../api/client';
import { PageHeader } from '../components/PageHeader';
import { PageContainer } from '../components/PageContainer';
import { WorkDeliveredCard } from '../components/home/WorkDeliveredCard';
import { TimeLedgerCard } from '../components/home/TimeLedgerCard';
import { VolumeStrip } from '../components/home/VolumeStrip';
import { NeedsALookCard } from '../components/home/NeedsALookCard';
import { FrictionQualityCard } from '../components/home/FrictionQualityCard';
import { WorkHeatmap } from '../components/home/WorkHeatmap';
import { LatestSessionsTable } from '../components/home/LatestSessionsTable';
import { ConnectDixiePanel } from '../components/home/ConnectDixiePanel';
import { text, cx } from '../styles/theme';
import type { TimePeriod } from '../types/api';
import { errorMessage } from '../utils/errorMessage';
import { formatTimestamp } from '../utils/format';

// A daemon that hasn't synced in this long is treated as stale — a stopped
// daemon otherwise looks identical to a quiet week.
const STALE_SYNC_SECONDS = 24 * 60 * 60;

// Kept out of the component body so the `Date.now()` read isn't flagged as an
// impure call during render (react-hooks/purity), mirroring formatTimestamp.
function syncIsStale(lastSyncSeconds: number): boolean {
  if (lastSyncSeconds <= 0) return false;
  return Date.now() / 1000 - lastSyncSeconds > STALE_SYNC_SECONDS;
}

const periods = [
  { id: 'today', label: 'Today' },
  { id: '7days', label: '7 Days' },
  { id: '30days', label: '30 Days' },
  { id: 'all', label: 'All Time' },
];

/**
 * Home is organized around questions, not metric tiles (per the 2026-07
 * redesign): what did I get (Work Delivered), where did the time go (Time
 * Ledger), raw volume for context, what needs me (Needs a Look), how did it
 * go (Friction & Quality), where the work went, and the latest sessions.
 */
export function Dashboard() {
  const [period, setPeriod] = useState<TimePeriod>('7days');
  const opts = { placeholderData: keepPreviousData } as const;

  const dashboard = useQuery({ queryKey: ['dashboard', period], queryFn: () => api.getDashboard(period), ...opts });
  const spend = useQuery({ queryKey: ['spendSummary', period], queryFn: () => api.getSpendSummary(period), ...opts });
  const activity = useQuery({ queryKey: ['agentActivity', period], queryFn: () => api.getAgentActivity(period), ...opts });
  const keys = useQuery({ queryKey: ['agentKeys', period], queryFn: () => api.getAgentKeys(period), ...opts });
  const look = useQuery({ queryKey: ['needsALook', period], queryFn: () => api.getNeedsALook(period), ...opts });
  const heatmap = useQuery({ queryKey: ['projectHeatmap', period], queryFn: () => api.getProjectHeatmap(period), ...opts });
  const conn = useQuery({ queryKey: ['connectionStatus'], queryFn: () => api.getConnectionStatus() });

  const hasNoActivity =
    !dashboard.isLoading && !!dashboard.data && parseInt(dashboard.data.sessions?.value ?? '0', 10) === 0;

  // Daemon connectivity, derived from GetConnectionStatus (the lightweight
  // landing-page query, #774). hasAnyUpload is omitted when false and
  // lastSyncTimestamp is a string omitted when zero, so default the absent
  // case. A connectivity error leaves both false and the normal dashboard
  // renders — connectivity hints never block the view.
  const lastSync = parseInt(conn.data?.lastSyncTimestamp ?? '0', 10);
  const neverConnected = !!conn.data && !conn.data.hasAnyUpload;
  const isStale = !!conn.data && !neverConnected && syncIsStale(lastSync);

  if (dashboard.error) {
    return (
      <PageContainer>
        <title>Dixie :: Home</title>
        <PageHeader title="Home" />
        <Alert variant="error">Failed to load dashboard: {errorMessage(dashboard.error)}</Alert>
      </PageContainer>
    );
  }

  // Never connected: the whole zeroed body (hero cards + VolumeStrip) would read
  // as broken, so replace it with the Connect panel. The period TabBar is
  // meaningless with no data, so drop it too.
  if (neverConnected) {
    return (
      <PageContainer>
        <title>Dixie :: Home</title>
        <PageHeader title="Home" />
        <ConnectDixiePanel />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <title>Dixie :: Home</title>
      <PageHeader title="Home" isFetching={dashboard.isFetching && !dashboard.isLoading}>
        <TabBar variant="pill" items={periods} activeId={period} onChange={(id: string) => setPeriod(id as TimePeriod)} />
      </PageHeader>

      {isStale && (
        <Alert variant="warning">
          Daemon last synced {formatTimestamp(String(lastSync))} — run "dixie status" to check that it's
          still running.
        </Alert>
      )}

      {/* Hero row: value + time. */}
      <div className="grid gap-6 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <WorkDeliveredCard data={spend.data} period={period} loading={spend.isLoading && !spend.data} />
        </div>
        <div className="lg:col-span-3">
          <TimeLedgerCard data={activity.data} period={period} loading={activity.isLoading && !activity.data} />
        </div>
      </div>

      {/* Raw volume for context. */}
      <VolumeStrip
        dashboard={dashboard.data}
        keys={keys.data}
        period={period}
        loading={dashboard.isLoading && !dashboard.data}
      />

      {hasNoActivity && (
        <Card>
          <Card.Body>
            <div className="py-10 text-center">
              <p className={cx('text-sm font-medium', text.primary)}>No session activity in this period</p>
              <p className={cx('mt-1 text-sm', text.secondary)}>
                Try a longer time range, or check that Claude Code is uploading session logs.
              </p>
            </div>
          </Card.Body>
        </Card>
      )}

      {!hasNoActivity && (
        <>
          {/* Attention + quality. */}
          <div className="grid gap-6 lg:grid-cols-2">
            <NeedsALookCard data={look.data} loading={look.isLoading && !look.data} />
            <FrictionQualityCard
              keys={keys.data}
              hashtags={dashboard.data?.topHashtags}
              loading={keys.isLoading && !keys.data}
            />
          </div>

          <WorkHeatmap data={heatmap.data} loading={heatmap.isLoading && !heatmap.data} />

          <LatestSessionsTable sessions={dashboard.data?.recentSessions ?? []} />
        </>
      )}
    </PageContainer>
  );
}
